import { ComponentChildren } from 'preact';
import { render } from 'preact-render-to-string';
import { getBrand } from 'shared/campaign-brands';
import { BasicEmailWrapper, Heading, Line, PrimaryAction, SupportFooter } from './shared';

export function checkinReminderSubject(courseTitle: string) {
  return `Reminder: today's standup for ${courseTitle}`;
}

type ReminderOptions = {
  firstName: string;
  courseTitle: string;
  checkinUrl: string;
  unsubscribeUrl?: string;
  /** This week's summary is due today and not sent yet. */
  missingSummaryWeek?: number;
  brandId?: string;
  supportEmail?: string;
};

/** The frame both reminders share: brand, support link and opt-out. */
function ReminderShell(opts: ReminderOptions & { children: ComponentChildren }) {
  const brand = getBrand(opts.brandId);
  return (
    <BasicEmailWrapper
      logoUrl={brand.defaultLogoUrl}
      logoAlt={brand.name}
      copyrightName={brand.copyrightName}
      footer={
        <>
          <SupportFooter email={opts.supportEmail} />
          {opts.unsubscribeUrl && (
            <>
              {' · '}
              <a
                href={opts.unsubscribeUrl}
                style="color:#003DA6;opacity:0.75;"
                target="_blank"
                rel="noreferrer"
              >
                Manage your email preferences
              </a>
            </>
          )}
        </>
      }
    >
      {opts.children}
    </BasicEmailWrapper>
  );
}

export function CheckinReminderEmail(opts: ReminderOptions) {
  return render(
    <ReminderShell {...opts}>
      <Heading>Time for today's standup</Heading>
      <Line>
        Hi {opts.firstName}, you haven't posted today's standup for{' '}
        <strong>{opts.courseTitle}</strong> yet. It takes about two minutes.
      </Line>
      {opts.missingSummaryWeek && (
        <Line>
          Your weekly summary for week {opts.missingSummaryWeek} is due tonight too. Run your weekly
          review, then paste the coach summary on the same page.
        </Line>
      )}
      <PrimaryAction href={opts.checkinUrl}>Post your standup</PrimaryAction>
    </ReminderShell>,
  );
}

export function summaryReminderSubject(courseTitle: string) {
  return `Reminder: your weekly summary for ${courseTitle}`;
}

/**
 * Sunday's reminder for a student who has posted the day's standup (or has
 * none due) but not their weekly summary.
 */
export function SummaryReminderEmail(opts: ReminderOptions) {
  return render(
    <ReminderShell {...opts}>
      <Heading>Your weekly summary is due tonight</Heading>
      <Line>
        Hi {opts.firstName}, you haven't sent your weekly summary for week {opts.missingSummaryWeek}{' '}
        of <strong>{opts.courseTitle}</strong> yet. Run your weekly review, then paste the coach
        summary on the Standups page.
      </Line>
      <PrimaryAction href={opts.checkinUrl}>Send your summary</PrimaryAction>
    </ReminderShell>,
  );
}
